import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, ArrowRight, Sparkles, Activity, Camera, Flame, Apple, Music, Target, Ruler, Timer } from "lucide-react";
import { api } from "../lib/api";
import BrandMark from "../components/BrandMark";

const FREE_FEATURES = [
  "Manual meal logging",
  "Weight & body measurements tracking",
  "View workouts you've already generated",
  "Personal records & workout history",
  "Spotify music pill",
];

const PRO_FEATURES = [
  { Icon: Activity, t: "AI workout plan generation (1–12 weeks)" },
  { Icon: Camera,   t: "Photo meal logging (Claude Vision)" },
  { Icon: Apple,    t: "AI text-to-meal estimator" },
  { Icon: Flame,    t: "AI micro-workouts from muscle heatmap" },
  { Icon: Target,   t: "Smart macros + dynamic TDEE recalculation" },
  { Icon: Sparkles, t: "Every future Pro-tier feature, free" },
];

export default function PricingPage() {
  const [plan, setPlan] = useState({ amount: 9.99, currency: "aud", period_days: 30 });

  useEffect(() => {
    api.get("/billing/plan").then(({ data }) => setPlan(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Nav (matches Landing's dark-island treatment) */}
      <nav className="dark-island fixed top-0 inset-x-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="pricing-brand">
            <BrandMark size={32} />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <Link to="/" className="hover:text-[#6B9BFF] transition-colors">Home</Link>
            <Link to="/#features" className="hover:text-[#6B9BFF] transition-colors">Features</Link>
            <Link to="/pricing" className="text-[#6B9BFF]">Pricing</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth" className="hidden sm:block text-sm text-white/70 hover:text-white" data-testid="pricing-signin">
              Sign in
            </Link>
            <Link
              to="/auth?mode=signup"
              className="bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-white transition-colors"
              data-testid="pricing-cta"
            >
              Start free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="inline-flex items-center gap-2 border border-[#6B9BFF]/30 bg-[#6B9BFF]/5 px-3 py-1.5 mb-6">
          <Crown className="w-3.5 h-3.5 text-[#6B9BFF]" />
          <span className="label-eyebrow text-[#6B9BFF]">Simple pricing</span>
        </div>
        <h1 className="font-display text-5xl sm:text-7xl leading-[0.9] tracking-tight mb-5 max-w-3xl">
          One month free.<br />
          <span className="text-white/50">Then $9.99 AUD.</span>
        </h1>
        <p className="text-white/70 max-w-xl text-lg leading-relaxed">
          Try every Pro feature for 30 days, no card required. After that,
          unlock unlimited AI for just ${plan.amount} {plan.currency.toUpperCase()} per month — cancel anytime.
        </p>
      </section>

      {/* Plan cards */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Free */}
          <div className="border border-white/10 bg-[#121212] p-8 lg:p-10" data-testid="pricing-card-free">
            <div className="label-eyebrow text-white/50 mb-3">Free</div>
            <div className="flex items-end gap-2 mb-1">
              <div className="font-display text-6xl leading-none">$0</div>
              <div className="text-white/50 text-sm mb-2">/forever</div>
            </div>
            <p className="text-white/60 text-sm mb-6">Track manually, view past workouts, listen to music.</p>
            <Link
              to="/auth?mode=signup"
              className="block w-full border border-white/15 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:border-[#6B9BFF] hover:text-[#6B9BFF] transition-colors text-center mb-6"
              data-testid="pricing-free-cta"
            >
              Create free account
            </Link>
            <ul className="space-y-2.5 text-sm text-white/70">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-white/40 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div
            className="tracing-border p-8 lg:p-10 relative"
            data-testid="pricing-card-pro"
          >
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 border border-[#6B9BFF]/40 bg-[#6B9BFF]/5 px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold text-[#6B9BFF]">
              <Crown className="w-3 h-3" /> Recommended
            </div>
            <div className="label-eyebrow text-[#6B9BFF] mb-3">Pro</div>
            <div className="flex items-end gap-2 mb-1">
              <div className="font-display text-6xl leading-none text-[#6B9BFF]">${plan.amount.toFixed(2)}</div>
              <div className="text-white/50 text-sm mb-2">{plan.currency.toUpperCase()} / month</div>
            </div>
            <div className="inline-flex items-center gap-1.5 mb-5 text-xs">
              <Sparkles className="w-3.5 h-3.5 text-[#6B9BFF]" />
              <span className="text-white/80 font-bold">First month free</span>
              <span className="text-white/40">· no card required</span>
            </div>
            <Link
              to="/auth?mode=signup"
              className="block w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors text-center mb-6"
              data-testid="pricing-pro-cta"
            >
              Start 30-day free trial
            </Link>
            <ul className="space-y-2.5 text-sm text-white/70">
              {PRO_FEATURES.map(({ Icon, t }) => (
                <li key={t} className="flex items-start gap-2">
                  <Icon className="w-4 h-4 text-[#6B9BFF] shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* FAQ-lite */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { q: "Is my card charged for the trial?", a: "No. We don't collect payment details until you choose to upgrade." },
            { q: "Can I cancel any time?", a: "Yes. Your Pro access continues until the end of the paid month, then reverts to Free." },
            { q: "What happens to my data on Free?", a: "Nothing — all your workouts, logs, and measurements stay. You just lose access to AI generation." },
          ].map((row) => (
            <div key={row.q} className="border border-white/10 bg-[#121212] p-6">
              <div className="font-display text-lg mb-2">{row.q}</div>
              <p className="text-white/60 text-sm leading-relaxed">{row.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <BrandMark size={24} textClassName="font-display tracking-wider text-white/70" />
          <div>© 2026 DAIONE · Train. Track. Transform.</div>
        </div>
      </footer>
    </div>
  );
}
